import { Link, useRouterState } from "@tanstack/react-router";
import { Phone } from "lucide-react";

import { FooterPlaceLinks } from "@/components/site/FooterPlaceLinks";
import { Logo } from "@/components/site/Logo";
import { nlPathForEn } from "@/lib/i18n-urls";
import { trackPhoneClick } from "@/lib/tracking";

const QUICK_LINKS = [
  { to: "/en/taxi", label: "Service area" },
  { to: "/en/book", label: "Book a taxi" },
  { to: "/en/prices", label: "Prices" },
  { to: "/en/airport-taxi", label: "Airport transfers" },
  { to: "/en/contact", label: "Contact" },
  { to: "/en/customer/login", label: "My rides" },
] as const;

const DRIVER_LINKS = [
  { to: "/en/partners", label: "Become a driver" },
  { to: "/partner/login", label: "Driver login" },
] as const;

const SERVICE_LINKS = [
  { to: "/en/about-us", label: "About us" },
  { to: "/en/journal", label: "Travel journal" },
  { to: "/en/faq", label: "Frequently asked questions" },
  { to: "/en/terms", label: "Terms and conditions" },
  { to: "/en/privacy", label: "Privacy & cookies" },
  { to: "/en/all-pages", label: "All pages" },
] as const;

const BUSINESS_LINKS = [
  { to: "/en/partners", label: "Business partners" },
  { to: "/partner/login", label: "Partner login" },
] as const;

export function EnFooter() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nl = nlPathForEn(pathname) ?? "/";

  return (
    <footer className="mt-20 border-t border-border bg-muted/40">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Link to="/en" className="inline-flex items-center">
              <Logo />
            </Link>
            <p className="mt-3 text-sm text-muted-foreground">
              Fixed prices, known before you book. Airport transfers and city rides throughout the
              Netherlands.
            </p>
            <a
              href="tel:+31850607086"
              onClick={() => trackPhoneClick("footer")}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              <Phone className="size-4" /> +31 85 060 7086
            </a>
            <p className="mt-3 text-sm">
              <a href={nl} hrefLang="nl-NL" className="text-muted-foreground hover:text-primary">
                Nederlandse versie
              </a>
            </p>
          </div>

          {[
            { title: "Quick links", items: QUICK_LINKS },
            { title: "For drivers", items: DRIVER_LINKS },
            { title: "Business", items: BUSINESS_LINKS },
            { title: "Service", items: SERVICE_LINKS },
          ].map((col) => (
            <div key={col.title}>
              <div className="text-sm font-semibold uppercase tracking-wide text-foreground">
                {col.title}
              </div>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                {col.items.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className="hover:text-primary">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <FooterPlaceLinks locale="en" />

      <div className="border-t border-border py-5 text-center text-xs text-muted-foreground">
        <p>
          © {new Date().getFullYear()} Taxiritje.nl — taxi travel at a fixed price.{" "}
          <Link to="/en/contact" className="underline underline-offset-2 hover:text-foreground">
            Company details
          </Link>
        </p>
      </div>
    </footer>
  );
}
